/**
 * Unwrap 0.99 SDK results (AxiosResponse / { data, error }) and map 0.34-style
 * flat arguments onto `{ path, query, body }`.
 */
import { AxiosError } from 'axios'
import { SDK_SHAPES, type SdkShape } from './sdkShapes.generated'
import * as sdk from '../../client/sdk.gen'

export class ApiError extends Error {
  readonly status?: number
  readonly body?: unknown
  readonly url?: string

  constructor(message: string, options?: { status?: number; body?: unknown; url?: string }) {
    super(message)
    this.name = 'ApiError'
    this.status = options?.status
    this.body = options?.body
    this.url = options?.url
  }
}

export function normalizeError(error: unknown): Error {
  if (error instanceof ApiError) {
    return error
  }
  if (error instanceof AxiosError) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail
    const message =
      typeof detail === 'string'
        ? detail
        : error.message || 'Request failed'
    const normalized = new ApiError(message, {
      status: error.response?.status,
      body: error.response?.data,
      url: error.config?.url,
    })
    ;(normalized as Error & { response?: unknown }).response = error.response
    return normalized
  }
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { detail?: string; status?: number; data?: unknown } })
      .response
    if (response?.detail) {
      const normalized = new Error(response.detail)
      ;(normalized as Error & { response?: unknown }).response = response
      return normalized
    }
  }
  if (error instanceof Error) {
    return error
  }
  return new Error('Unknown API error')
}

function isSdkOptions(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const record = value as Record<string, unknown>
  return (
    'path' in record ||
    'query' in record ||
    'body' in record ||
    'headers' in record ||
    'client' in record
  )
}

function snakeToCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/** FastAPI query names are snake_case; façade callers often pass camelCase. */
function assignQueryLeftovers(
  query: Record<string, unknown>,
  leftovers: Record<string, unknown>,
): void {
  for (const [key, value] of Object.entries(leftovers)) {
    query[key] = value
    const snake = camelToSnake(key)
    if (snake !== key && query[snake] === undefined) {
      query[snake] = value
    }
  }
}

function readLegacyField(
  legacy: Record<string, unknown>,
  key: string,
): unknown {
  if (key in legacy) {
    return legacy[key]
  }
  const camel = snakeToCamel(key)
  if (camel in legacy) {
    return legacy[camel]
  }
  const snake = camelToSnake(key)
  if (snake in legacy) {
    return legacy[snake]
  }
  return undefined
}

const CONSUMED_WRAPPER_KEYS = new Set([
  'requestBody',
  'formData',
  'body',
  'path',
  'query',
  'headers',
  'client',
])

export function toSdkOptions(
  fnName: string,
  legacy?: unknown,
): Record<string, unknown> | undefined {
  if (legacy == null) {
    return undefined
  }
  if (isSdkOptions(legacy)) {
    const record = { ...(legacy as Record<string, unknown>) }
    if ('requestBody' in record && record.body === undefined) {
      record.body = record.requestBody
      delete record.requestBody
    }
    if ('formData' in record && record.body === undefined) {
      record.body = record.formData
      delete record.formData
    }
    return record
  }
  if (typeof legacy !== 'object' || Array.isArray(legacy)) {
    return { body: legacy }
  }

  const source = legacy as Record<string, unknown>
  const shape: SdkShape | undefined = SDK_SHAPES[fnName]
  const path: Record<string, unknown> = {}
  const query: Record<string, unknown> = {}
  const consumed = new Set<string>()

  if (shape) {
    for (const key of shape.path) {
      const value = readLegacyField(source, key)
      if (value !== undefined) {
        path[key] = value
        consumed.add(key)
        consumed.add(snakeToCamel(key))
        consumed.add(camelToSnake(key))
      }
    }
    for (const key of shape.query) {
      const value = readLegacyField(source, key)
      if (value !== undefined) {
        query[key] = value
        consumed.add(key)
        consumed.add(snakeToCamel(key))
        consumed.add(camelToSnake(key))
      }
    }
  }

  let body = source.requestBody ?? source.formData ?? source.body
  if (body !== undefined) {
    consumed.add('requestBody')
    consumed.add('formData')
    consumed.add('body')
  }

  const leftovers: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(source)) {
    if (CONSUMED_WRAPPER_KEYS.has(key) || consumed.has(key)) {
      continue
    }
    leftovers[key] = value
  }

  if (!shape) {
    assignQueryLeftovers(query, leftovers)
  } else if (!shape.hasBody && body === undefined) {
    assignQueryLeftovers(query, leftovers)
  } else if (shape.hasBody && body === undefined && Object.keys(leftovers).length > 0) {
    const mapped: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(leftovers)) {
      mapped[key] = value
      const snake = camelToSnake(key)
      if (snake !== key) {
        mapped[snake] = value
      }
    }
    body = mapped
  }

  const options: Record<string, unknown> = {}
  if (Object.keys(path).length > 0) {
    options.path = path
  }
  if (Object.keys(query).length > 0) {
    options.query = query
  }
  if (body !== undefined) {
    options.body = body
  }
  return Object.keys(options).length > 0 ? options : undefined
}

export async function sdkData<T = any>(pending: PromiseLike<{ data?: T; error?: unknown }>): Promise<T> {
  try {
    const result = await pending
    if (result && typeof result === 'object' && 'error' in result && result.error) {
      throw normalizeError(result.error)
    }
    return result.data as T
  } catch (error) {
    throw normalizeError(error)
  }
}

const METHOD_ALIASES: Record<string, string> = {
  listPromptSlots: 'listAiPromptSlotsRoute',
  disableTaskSchedule: 'deleteTaskSchedule',
  getDashboardSummary: 'getAiclaimsDashboardSummary',
  getWorkflowCards: 'getAiclaimsWorkflowCards',
  generateWorkflowBuilderPrompt: 'generatePrompt',
  generateWorkflowBuilderWorkflow: 'generateWorkflow',
  generateWorkflowBuilderOutput: 'generateOutput',
  commitWorkflowBuilder: 'commitWorkflow',
  getRecentRuns: 'getRecentSummaryRuns',
  runCallAuditBatch: 'auditBatchEndpoint',
  listCallAuditBatches: 'listAuditBatchesEndpoint',
  getCallAuditBatch: 'getAuditBatchEndpoint',
  rerunCallAuditBatch: 'auditRerunEndpoint',
  usersSetActiveBrandMe: 'setActiveBrandMe',
  generateApiKeyForUserWithBody: 'generateApiKeyForUser',
  updateTableId: 'updateTableRow',
  deleteTableId: 'deleteTableRow',
}

type ResolvedSdkFunction = {
  fn: (options?: unknown) => PromiseLike<{ data?: unknown; error?: unknown }>
  /** SDK module export key — stable under minify, unlike Function.name. */
  exportName: string
}

function resolveSdkFunction(
  serviceName: string,
  prop: string,
): ResolvedSdkFunction | undefined {
  const prefix = serviceName.replace(/Service$/, '')
  const camelPrefix = prefix.charAt(0).toLowerCase() + prefix.slice(1)
  const aliased = METHOD_ALIASES[prop] ?? prop
  const candidates = [
    aliased,
    prop,
    camelPrefix + aliased.charAt(0).toUpperCase() + aliased.slice(1),
    camelPrefix + prop.charAt(0).toUpperCase() + prop.slice(1),
  ]
  const sdkRecord = sdk as Record<string, unknown>
  for (const name of candidates) {
    const fn = sdkRecord[name]
    if (typeof fn === 'function') {
      return {
        fn: fn as (options?: unknown) => PromiseLike<{ data?: unknown; error?: unknown }>,
        exportName: name,
      }
    }
  }
  return undefined
}

/** Loose body so façade callers keep property access without implicit any. */
export type SdkRow = {
  [key: string]: any
}

export type SdkBody = {
  data?: SdkRow[]
  count?: number
  [key: string]: any
}

type SdkMethod = (...args: any[]) => Promise<SdkBody>
export type SdkFacade = {
  [key: string]: SdkMethod
}

export function createSdkWrapper(serviceName: string): any {
  const bind = (prop: string) => {
    const resolved = resolveSdkFunction(serviceName, prop)
    if (!resolved) {
      return undefined
    }
    // Use the SDK export key, not Function.name. Production minify mangles
    // .name, which used to skip SDK_SHAPES and send `/referrals/{id}?id=`.
    const fnName = resolved.exportName
    return async (legacy?: unknown) => {
      try {
        return await sdkData(resolved.fn(toSdkOptions(fnName, legacy)))
      } catch (error) {
        throw normalizeError(error)
      }
    }
  }

  const handler: ProxyHandler<object> = {
    get(_target, prop) {
      if (typeof prop !== 'string' || prop === 'then') {
        return undefined
      }
      return bind(prop)
    },
  }

  const ApiWrapper = function ApiWrapper() {
    return new Proxy({}, handler)
  } as any

  return new Proxy(ApiWrapper, {
    get(target, prop, receiver) {
      if (prop === 'prototype' || prop === 'name' || typeof prop === 'symbol') {
        return Reflect.get(target, prop, receiver)
      }
      if (typeof prop === 'string') {
        const bound = bind(prop)
        if (bound) {
          return bound
        }
      }
      return Reflect.get(target, prop, receiver)
    },
    construct() {
      return new Proxy({}, handler)
    },
  })
}
